import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import api from '../../../api/axios';

// ─── Color / config maps ────────────────────────────────────────────────────
const STATUS_CFG = {
  Delivered:    { badge: 'bg-green-100 text-green-700',  icon: 'CheckCircle2' },
  RTO:          { badge: 'bg-red-100 text-red-700',      icon: 'XCircle' },
  Pending:      { badge: 'bg-amber-100 text-amber-700',  icon: 'Clock' },
  Rescheduled:  { badge: 'bg-blue-100 text-blue-700',    icon: 'CalendarClock' },
  Denied:       { badge: 'bg-red-100 text-red-600',      icon: 'Ban' },
  'No Response':{ badge: 'bg-gray-100 text-gray-600',    icon: 'PhoneMissed' },
};

const EVENT_ICON = {
  call:               { name: 'Phone',        bg: 'bg-blue-100',   color: '#2563eb' },
  ndr_event:          { name: 'Package',      bg: 'bg-amber-100',  color: '#d97706' },
  call_outcome:       { name: 'PhoneCall',    bg: 'bg-purple-100', color: '#7c3aed' },
  status_change:      { name: 'RefreshCw',    bg: 'bg-green-100',  color: '#16a34a' },
  email_sent:         { name: 'Mail',         bg: 'bg-pink-100',   color: '#db2777' },
  callback_scheduled: { name: 'CalendarClock',bg: 'bg-teal-100',   color: '#0d9488' },
};

const ATTEMPT_COLORS = [
  { border: 'border-blue-200',   label: 'text-blue-700',   bg: 'bg-blue-50',   dot: 'bg-blue-500' },
  { border: 'border-purple-200', label: 'text-purple-700', bg: 'bg-purple-50', dot: 'bg-purple-500' },
  { border: 'border-amber-200',  label: 'text-amber-700',  bg: 'bg-amber-50',  dot: 'bg-amber-500' },
];

// ─── Audio Player ─────────────────────────────────────────────────────────────
const MiniAudioPlayer = ({ url }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play(); setPlaying(true); }
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => { if (audio.duration) setProgress((audio.currentTime / audio.duration) * 100); };
    const onEnd = () => { setPlaying(false); setProgress(0); };
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('ended', onEnd);
    return () => { audio.removeEventListener('timeupdate', onTime); audio.removeEventListener('ended', onEnd); };
  }, []);

  return (
    <div className="mt-2 flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
      <audio ref={audioRef} src={url} preload="none" />
      <button onClick={toggle} className="w-7 h-7 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 hover:bg-blue-700 transition-colors">
        <Icon name={playing ? 'Pause' : 'Play'} size={12} color="#fff" />
      </button>
      <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
        <div className="h-full bg-blue-500 rounded-full transition-all duration-100" style={{ width: `${progress}%` }} />
      </div>
      <Icon name="Headphones" size={13} color="#6b7280" />
    </div>
  );
};

// ─── Single Timeline Event ────────────────────────────────────────────────────
const TimelineEvent = ({ event }) => {
  const cfg = EVENT_ICON[event?.type] || EVENT_ICON.ndr_event;

  const renderContent = () => {
    switch (event?.type) {
      case 'call': {
        const isNoConnect = event.callStatus === 'no-answer' || event.callStatus === 'busy';
        const displayStatus = isNoConnect ? "Couldn't Connect" : event.callStatus;
        const outcomeColor =
          event.callStatus === 'completed' ? 'text-green-600' :
          event.callStatus === 'failed' || isNoConnect ? 'text-red-500' : 'text-gray-500';
        const duration = event.durationSeconds
          ? `${Math.floor(event.durationSeconds / 60)}m ${Math.round(event.durationSeconds % 60)}s`
          : null;
        const actionColor =
          event.postCallActionStatus === 'TRIGGERED' ? 'text-green-600 bg-green-50 border-green-200' :
          event.postCallActionStatus === 'CANCELLED' ? 'text-red-500 bg-red-50 border-red-200' :
          'text-blue-600 bg-blue-50 border-blue-200';
        return (
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold text-gray-800">Call</span>
              <span className={`text-xs font-medium ${outcomeColor}`}>· {displayStatus}</span>
              {duration && <span className="text-xs text-gray-400">· {duration}</span>}
            </div>
            <p className="text-xs text-gray-400 mt-0.5">{event.timestamp}</p>
            {event.callSummary && (
              <p className="text-xs text-gray-600 mt-1 bg-gray-50 rounded px-2 py-1 border border-gray-100 line-clamp-3">
                {event.callSummary}
              </p>
            )}
            {event.recordingUrl && <MiniAudioPlayer url={event.recordingUrl} />}
            {event.postCallAction && (
              <div className={`mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${actionColor}`}>
                <Icon name="RotateCw" size={10} />
                {event.postCallAction}
              </div>
            )}
          </div>
        );
      }
      case 'ndr_event':
        return (
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-800">NDR Event</span>
              {event.shipwayStatusCode && (
                <span className="text-xs px-1.5 py-0.5 rounded font-medium bg-amber-50 text-amber-600">
                  {event.shipwayStatusCode}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-0.5">{event.timestamp}</p>
            {event.ndrReason && <p className="text-xs text-gray-700 mt-1 font-medium">{event.ndrReason}</p>}
          </div>
        );
      case 'call_outcome': {
        const coCfg = STATUS_CFG[event.ndrStatus] || STATUS_CFG['Pending'];
        const isDeliveryReschedule = event.ndrStatus === 'Rescheduled';
        const outcomeLabel = isDeliveryReschedule ? 'Delivery Rescheduled' : 'Call Outcome';
        return (
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-800">{outcomeLabel}</span>
              {!isDeliveryReschedule && (
                <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${coCfg?.badge}`}>{event.ndrStatus}</span>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-0.5">{event.timestamp}</p>
            {event.rescheduledSlot && (
              <p className="text-xs text-indigo-600 font-medium mt-1">Slot: {event.rescheduledSlot}</p>
            )}
          </div>
        );
      }
      case 'status_change': {
        const sCfg = STATUS_CFG[event.ndrStatus] || STATUS_CFG['Pending'];
        return (
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-800">Delivery Updated</span>
              <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${sCfg?.badge}`}>{event.ndrStatus}</span>
            </div>
            <p className="text-xs text-gray-400 mt-0.5">{event.timestamp}</p>
          </div>
        );
      }
      case 'email_sent':
        return (
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-800">Email Sent</span>
              <span className="text-xs px-1.5 py-0.5 rounded font-medium bg-pink-50 text-pink-600">Sent</span>
            </div>
            <p className="text-xs text-gray-400 mt-0.5">{event.timestamp}</p>
            {event.emailRecipient && (
              <p className="text-xs text-gray-600 mt-1">
                <span className="text-gray-400">To:</span> {event.emailRecipient}
              </p>
            )}
            {event.emailSubject && (
              <p className="text-xs text-gray-600 mt-0.5 bg-gray-50 rounded px-2 py-1 border border-gray-100 line-clamp-2">
                {event.emailSubject}
              </p>
            )}
          </div>
        );
      case 'callback_scheduled': {
        const statusColor =
          event.callbackStatus === 'TRIGGERED' ? 'bg-green-50 text-green-600' :
          event.callbackStatus === 'CANCELLED' ? 'bg-red-50 text-red-600' :
          'bg-teal-50 text-teal-600';
        const statusLabel =
          event.callbackStatus === 'TRIGGERED' ? 'Triggered' :
          event.callbackStatus === 'CANCELLED' ? 'Cancelled' : 'Pending';
        return (
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-800">Follow-up Call Scheduled</span>
              <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${statusColor}`}>{statusLabel}</span>
            </div>
            <p className="text-xs text-gray-400 mt-0.5">{event.timestamp}</p>
            {event.scheduledAt && (
              <p className="text-xs text-teal-600 font-medium mt-1">
                <span className="text-gray-400">Scheduled for:</span> {event.scheduledAt}
              </p>
            )}
            {event.callbackReason && (
              <p className="text-xs text-gray-600 mt-0.5 bg-gray-50 rounded px-2 py-1 border border-gray-100 line-clamp-2">
                {event.callbackReason}
              </p>
            )}
          </div>
        );
      }
      default:
        return null;
    }
  };

  return (
    <div className="relative flex gap-3 pl-8">
      <div className={`absolute left-0 top-0.5 w-6 h-6 rounded-full ${cfg?.bg} flex items-center justify-center flex-shrink-0 z-10`}>
        <Icon name={cfg?.name} size={12} color={cfg?.color} />
      </div>
      <div className="flex-1 pb-4">{renderContent()}</div>
    </div>
  );
};

// ─── Attempt Section (collapsible) ───────────────────────────────────────────
const AttemptSection = ({ attempt, events, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  const ac = ATTEMPT_COLORS[(attempt.attemptNumber - 1) % 3];
  const outcomeCfg = attempt.callOutcome ? (STATUS_CFG[attempt.callOutcome] || STATUS_CFG['Pending']) : null;
  const callCount = events.filter((e) => e.type === 'call').length;

  return (
    <div className={`rounded-xl border ${ac.border} overflow-hidden mb-4`}>
      {/* Attempt header */}
      <button
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center justify-between px-4 py-3 ${ac.bg} hover:brightness-95 transition-all`}
      >
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${ac.dot}`} />
          <span className={`text-sm font-bold ${ac.label}`}>NDR Attempt {attempt.attemptNumber}</span>
        </div>
        <div className="flex items-center gap-3">
          {outcomeCfg && (
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${outcomeCfg.badge}`}>
              {attempt.callOutcome}
            </span>
          )}
          {callCount > 0 && (
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <Icon name="Phone" size={11} color="#6b7280" />
              {callCount} call{callCount !== 1 ? 's' : ''}
            </span>
          )}
          <Icon name={open ? 'ChevronUp' : 'ChevronDown'} size={15} color="#6b7280" />
        </div>
      </button>

      {/* Timeline body */}
      {open && (
        <div className="px-4 pt-4 pb-1 bg-white">
          {/* Attempt summary row */}
          <div className="flex flex-wrap gap-x-6 gap-y-1 mb-4 px-1">
            {attempt.ndrReason && (
              <div>
                <span className="text-xs text-gray-400">NDR Reason</span>
                <p className="text-xs font-semibold text-gray-700">{attempt.ndrReason}</p>
              </div>
            )}
            {attempt.rescheduledSlot && (
              <div>
                <span className="text-xs text-gray-400">Rescheduled Slot</span>
                <p className="text-xs font-semibold text-indigo-600">{attempt.rescheduledSlot}</p>
              </div>
            )}
            {attempt.status && (
              <div>
                <span className="text-xs text-gray-400">Delivery Status</span>
                <p className="text-xs font-semibold text-gray-700">{attempt.status}</p>
              </div>
            )}
          </div>

          {events.length === 0 ? (
            <p className="text-xs text-gray-400 py-2 text-center">No events recorded for this attempt.</p>
          ) : (
            <div className="relative">
              <div className="absolute left-3 top-0 bottom-0 w-px bg-gray-100" />
              <div className="flex flex-col">
                {events.map((ev, i) => (
                  <TimelineEvent key={i} event={ev} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Group timeline events by attempt ────────────────────────────────────────
function groupEventsByAttempt(timeline, attempts) {
  const groups = {};
  attempts.forEach((a) => { groups[a.attemptNumber] = []; });

  // Build attempt timestamp ranges for assigning calls (attemptNumber=0)
  const sortedAttempts = [...attempts].filter((a) => a.ndrReason).sort((a, b) => a.attemptNumber - b.attemptNumber);

  const timestampAssignableTypes = new Set(['call', 'email_sent', 'callback_scheduled']);

  timeline.forEach((ev) => {
    const an = ev.attemptNumber || 0;
    if (an > 0 && groups[an]) {
      groups[an].push(ev);
    } else if (an === 0 && timestampAssignableTypes.has(ev.type)) {
      // Assign to the closest attempt by comparing timestamps
      let assigned = false;
      const evTime = ev.timestamp ? new Date(ev.timestamp).getTime() : 0;
      for (let i = sortedAttempts.length - 1; i >= 0; i--) {
        const aTime = sortedAttempts[i].timestamp ? new Date(sortedAttempts[i].timestamp).getTime() : 0;
        if (evTime >= aTime || i === 0) {
          const key = sortedAttempts[i].attemptNumber;
          if (groups[key]) { groups[key].push(ev); assigned = true; }
          break;
        }
      }
      // Fallback: put in last attempt
      if (!assigned && sortedAttempts.length > 0) {
        const last = sortedAttempts[sortedAttempts.length - 1].attemptNumber;
        if (groups[last]) groups[last].push(ev);
      }
    }
  });

  // Sort events within each group — latest on top (descending)
  Object.values(groups).forEach((evts) => {
    evts.sort((a, b) => {
      const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return tb - ta;
    });
  });

  return groups;
}

// ─── Main Drawer ──────────────────────────────────────────────────────────────
const NDRDrawer = ({ order, onClose }) => {
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!order?.orderId) return;
    let cancelled = false;
    setLoading(true);
    api
      .get(`/api/v1/ndr-management/orders/${encodeURIComponent(order.orderId)}/timeline`)
      .then((res) => { if (!cancelled) setTimeline(res.data?.data || []); })
      .catch((err) => { console.error('NDR timeline fetch failed:', err); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [order?.orderId]);

  if (!order) return null;

  const attempts = order.attempts || [];
  const activeAttempts = attempts.filter((a) => a?.ndrReason);
  const groupedEvents = groupEventsByAttempt(timeline, attempts);

  // Find last active attempt to default-open
  const lastActiveIdx = activeAttempts.length > 0
    ? activeAttempts[activeAttempts.length - 1].attemptNumber
    : 1;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 z-40 backdrop-blur-sm" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-50 shadow-2xl flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b border-gray-100 bg-gray-50 flex-shrink-0">
          <div>
            <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">NDR Timeline</p>
            <p className="text-base font-bold text-gray-900 font-mono">{order.orderId}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-200 transition-colors">
            <Icon name="X" size={18} color="#6b7280" />
          </button>
        </div>

        {/* Order Info */}
        <div className="px-4 md:px-6 py-4 border-b border-gray-100 bg-white flex-shrink-0">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-gray-400">Customer</p>
              <p className="text-sm font-semibold text-gray-800">{order.customerName}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Mobile</p>
              <p className="text-sm font-semibold text-blue-600">{order.phone}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Order Value</p>
              <p className="text-sm font-bold text-gray-900">{order.orderValue}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Total Attempts</p>
              <p className="text-sm font-semibold text-gray-800">{activeAttempts.length}</p>
            </div>
          </div>
        </div>

        {/* Attempt Sections */}
        <div className="flex-1 overflow-y-auto px-4 md:px-5 py-5">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-4">NDR Attempt History</p>

          {loading ? (
            <div className="flex flex-col gap-4">
              {Array.from({ length: 2 }).map((_, i) => (
                <div key={i} className="rounded-xl border border-gray-200 p-4 animate-pulse">
                  <div className="h-5 bg-gray-100 rounded w-40 mb-3" />
                  <div className="h-4 bg-gray-100 rounded w-64 mb-2" />
                  <div className="h-4 bg-gray-100 rounded w-48" />
                </div>
              ))}
            </div>
          ) : (
            <>
              {/* Active attempts — latest first */}
              {[...activeAttempts].reverse().map((attempt) => (
                <AttemptSection
                  key={attempt.attemptNumber}
                  attempt={attempt}
                  events={groupedEvents[attempt.attemptNumber] || []}
                  defaultOpen={attempt.attemptNumber === lastActiveIdx}
                />
              ))}
              {/* Inactive attempts — not yet initiated */}
              {attempts.filter((a) => !a.ndrReason).map((attempt, idx) => (
                <div key={`inactive-${idx}`} className="rounded-xl border border-dashed border-gray-200 px-4 py-3 mb-4 flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-gray-300" />
                  <span className="text-xs text-gray-400 font-medium">NDR Attempt {attempt.attemptNumber || idx + 1} — Not yet initiated</span>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 md:px-6 py-4 border-t border-gray-100 bg-gray-50 flex gap-3 flex-shrink-0">
          <button className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors">
            <Icon name="Phone" size={15} />
            Call Customer
          </button>
          <button className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-100 transition-colors">
            <Icon name="ExternalLink" size={15} />
            CRM View
          </button>
        </div>
      </div>
    </>
  );
};

export default NDRDrawer;
